import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { apolloService } from '@/apollo/apolloRequests';
import { Query, AccountingReportType } from '@/generated/graphql';
import { queries } from '@/apollo';

export const useCompanyAutopilotStore = defineStore('companyAutopilot', () => {
  const companyId = ref<string | null>(null);
  const isAutopilotClient = ref(false);
  const rawPeriods = ref<AccountingReportType[]>([]);
  const isLoading = ref(false);

  const periodsSorted = computed(() => {
    return [...rawPeriods.value].sort((a, b) => {
      const yearA = a.year ?? 0;
      const yearB = b.year ?? 0;
      if (yearB !== yearA) return yearB - yearA;
      const monthA = a.month ?? 0;
      const monthB = b.month ?? 0;
      return monthB - monthA;
    });
  });

  const fetchCompanyData = async (id: string) => {
    if (!id) return;

    // Do not keep the flag of the previously opened company while the new one is loading
    if (companyId.value !== id) {
      isAutopilotClient.value = false;
    }
    companyId.value = id;
    isLoading.value = true;
    rawPeriods.value = [];

    try {
      const data = (await apolloService.makeApolloRequest(
        'query',
        queries.GET_COMPANY_WITH_FOLDERS,
        { idCompany: id },
      )) as Query;

      const result = data?.accounting?.getCompanyWithFolders;
      // Ignore an outdated response if another company has been requested in the meantime
      if (!result || companyId.value !== id) return;

      const company = result.company as { isAutopilotClient?: boolean } | undefined;
      isAutopilotClient.value = company?.isAutopilotClient === true;

      const allPeriods: AccountingReportType[] = [];
      result.folders?.forEach((folder) => {
        folder.periods?.forEach((p) => {
          if (p && 'id' in p) {
            allPeriods.push(p as AccountingReportType);
          }
        });
      });
      rawPeriods.value = allPeriods;
    } catch (error) {
      console.error('Error fetching company autopilot data:', error);
      isAutopilotClient.value = false;
      rawPeriods.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  const clear = () => {
    companyId.value = null;
    isAutopilotClient.value = false;
    rawPeriods.value = [];
  };

  return {
    companyId,
    isAutopilotClient,
    periodsSorted,
    isLoading,
    fetchCompanyData,
    clear,
  };
});
